import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'
import type {
	QRCareCenterHookResult,
	QRCareCenterState,
	MenuChatItem,
	ModalData
} from '../types/qrCareCenter.types'
import { NO_EXECUTE_CONTENTFULL_ROUTES } from 'utils/utils'

/**
 * Hook para manejar los datos de QR Care Center
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {QRCareCenterHookResult} Datos procesados y estados del hook
 */
export const useQRCareCenter = (): QRCareCenterHookResult => {
	const pathname = window.location.pathname
	const isExceptionRoute = NO_EXECUTE_CONTENTFULL_ROUTES.includes(pathname)
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'qrCareCenter'] as const,
		queryFn: queriesApi.getQRCareCenter,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		enabled: !isExceptionRoute
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de QR Care Center desde Contentful:', error)
	}

	// Función para distribuir el menú del chat
	const menuDistributor = (
		titles: (string | undefined)[],
		content: (string | undefined)[]
	): MenuChatItem[] => {
		return titles.map((title, index) => ({
			title: title || '',
			textButtons: content[index] || ''
		}))
	}

	// Función para distribuir datos de modales
	const modalDistributions = (data: any): ModalData => {
		if (!data) return {}

		const {
			imagenPrincipal: { url } = null,
			tituloGeneral = null,
			tituloDescripcion = null,
			botonCollection: { items } = { items: [] }
		} = data

		const setButtons = (buttons: any[]) => {
			if (!buttons || buttons.length === 0) return

			return buttons.map(button => ({
				...button,
				title: button.tituloBoton
			}))
		}

		return {
			img: url,
			title: tituloGeneral,
			text: tituloDescripcion,
			buttons: setButtons(items)
		}
	}

	// Procesar datos usando memoización para evitar recálculos innecesarios
	const processedData = useMemo((): QRCareCenterState => {
		if (!rawData || isLoading || error) {
			return {
				menuChatContent: false,
				noHaveCareCenterPlan: false,
				noTicketsRemaining: false,
				unActive: false,
				noPermission: false
			}
		}

		try {
			const contentItems =
				rawData?.templateContenidoCollection?.items?.[0]?.referenciaContenidoCollection?.items

			if (!contentItems || contentItems.length === 0) {
				return {
					menuChatContent: false,
					noHaveCareCenterPlan: false,
					noTicketsRemaining: false,
					unActive: false,
					noPermission: false
				}
			}

			const menuFromContent = contentItems[0]

			return {
				menuChatContent: menuDistributor(
					[menuFromContent?.tituloGeneral, menuFromContent?.tituloDescripcion],
					[menuFromContent?.identificador1, menuFromContent?.identificador2]
				),
				noHaveCareCenterPlan: modalDistributions(contentItems[1]),
				noTicketsRemaining: modalDistributions(contentItems[12]),
				unActive: modalDistributions(contentItems[11]),
				noPermission: modalDistributions(contentItems[13])
			}
		} catch (transformError) {
			console.error('Error al procesar datos de QR Care Center:', transformError)
			return {
				menuChatContent: false,
				noHaveCareCenterPlan: false,
				noTicketsRemaining: false,
				unActive: false,
				noPermission: false
			}
		}
	}, [rawData, isLoading, error])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 días desde ahora
			lastUpdated: rawData ? new Date() : new Date()
		}),
		[rawData]
	)

	return {
		// Datos procesados
		...processedData,

		// Estados de la query
		loading: isLoading,
		error: error as Error | null,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}

export default useQRCareCenter
